import React, { PropsWithChildren, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { LanguageMenuItem, Link, SkipLink } from 'suomifi-ui-components'
import { addQueryParameters } from 'common/src/vtj/browser/query-param.util'
import { FI, SV, EN } from 'common/src/vtj/browser/localization'
import { LanguageCode } from 'common/src/vtj/LanguageCode.enum'
import { TodistuksetUser } from 'todistukset-common/src/vtj/types/user'
import LocalizedString from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'
import {
  PageHeader,
  InnerHeader,
  TitleLink,
  ProfileMenu,
  UserInfo,
  LanguageWrapper,
  TestAuthenticatorButton,
  LoginIcon,
  LoginButtonLink,
  StyledLanguageMenu,
  DvvIcon,
  DvvLogo,
} from 'todistukset-ui/src/vtj/components/header/Header.sc'

export interface HeaderProps {
  lang: LanguageCode
  selectLanguage: (language: LanguageCode) => void
  user?: TodistuksetUser
  loginHref: string
  logoutHref: string
  testAuthenticatorHref?: string
  isErrorPage: boolean // On error pages, navigation links need to reload the page to clear the error state from store
}

const Header = (props: PropsWithChildren<HeaderProps>): React.ReactElement => {
  const { lang, selectLanguage, children, isErrorPage } = props

  // Focus management on landing with hash #top
  const { hash } = useLocation()
  const topRef = useRef<HTMLElement>(null)
  useEffect(() => {
    if (hash === '#top') {
      topRef.current?.scrollIntoView()
      topRef.current?.focus()
    }
  }, [hash])
  const { t } = useTranslation()

  return (
    <PageHeader
      data-test-id="page-header"
      variant="header"
      tabIndex={-1}
      ref={topRef}
      id="top"
    >
      <SkipLink href="#main-content" data-test-id="navigateToContent">
        <LocalizedString stringKey="navigateToContent" />
      </SkipLink>
      {children}
      <InnerHeader>
        <DvvIcon src={`/public/assets/images/dvv-symboli.svg`} alt={t('dvv')} />
        <DvvLogo
          src={`/public/assets/images/logo-dvv-white-${lang}.svg`}
          alt={t('dvv')}
        />
        <TitleLink
          to={`/${lang}/`}
          data-test-id="header-title-link"
          role="link"
          reloadDocument={isErrorPage}
        >
          <LocalizedString stringKey="headerMainTitle" />
        </TitleLink>
        <HeaderLanguageChange lang={lang} selectLanguage={selectLanguage} />
        <HeaderProfileMenu {...props} />
      </InnerHeader>
    </PageHeader>
  )
}

type HeaderProfileMenuProps = Pick<
  HeaderProps,
  'lang' | 'user' | 'logoutHref' | 'loginHref' | 'testAuthenticatorHref'
>

const HeaderProfileMenu = (props: HeaderProfileMenuProps) => {
  const { lang, user, logoutHref, loginHref, testAuthenticatorHref } = props
  const isLoggedIn = !!user

  return (
    <ProfileMenu>
      {isLoggedIn ? (
        <UserInfo>
          <div data-test-id="user-display-name">
            <span data-test-id="user-first-names">{user.firstNames}</span>{' '}
            <span data-test-id="user-last-name">{user.lastName}</span>
          </div>
          <Link
            data-test-id="logout-link"
            href={addQueryParameters(logoutHref, { lang })}
            tabIndex={0}
          >
            <LocalizedString stringKey="headerLogoutButtonLabel" />
          </Link>
        </UserInfo>
      ) : (
        <>
          <LoginButtonLink data-test-id="login-link" href={loginHref}>
            <>
              <LoginIcon />
              <LocalizedString stringKey="headerLoginButtonLabel" />
            </>
          </LoginButtonLink>
          {testAuthenticatorHref && (
            <TestAuthenticatorButton
              data-test-id="test-authenticator"
              href={testAuthenticatorHref}
              tabIndex={0}
            >
              <LoginIcon />
              Testitunnistaja
            </TestAuthenticatorButton>
          )}
        </>
      )}
    </ProfileMenu>
  )
}

const languages = [
  { lang: FI, label: 'Suomeksi (FI)' },
  { lang: SV, label: 'På svenska (SV)' },
  { lang: EN, label: 'In English (EN)' },
]

const HeaderLanguageChange = (props: {
  lang: LanguageCode
  selectLanguage: (lang: LanguageCode) => void
}) => {
  const { lang: selectedLanguage, selectLanguage } = props
  const { t } = useTranslation()
  return (
    <LanguageWrapper>
      <StyledLanguageMenu
        buttonText={
          languages.find(({ lang }) => lang === selectedLanguage)?.label ?? ''
        }
        data-test-id="change-language-button"
        aria-label={t('chooseLanguage')}
      >
        {languages.map((language) => {
          const { lang, label } = language
          return (
            <LanguageMenuItem
              onSelect={() => selectLanguage(lang)}
              key={lang}
              selected={lang === selectedLanguage}
              data-test-id={`change-language-item-${lang}`}
              lang={lang}
            >
              {label}
            </LanguageMenuItem>
          )
        })}
      </StyledLanguageMenu>
    </LanguageWrapper>
  )
}

export default Header
